import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { QueryClient, QueryClientProvider } from 'react-query';
import { Amplify } from 'aws-amplify';
import outputs from './amplify_outputs.json';

import './index.css';
import * as serviceWorkerRegistration from './serviceWorkerRegistration';

import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import localeData from 'dayjs/plugin/localeData';
import weekday from 'dayjs/plugin/weekday';
import weekOfYear from 'dayjs/plugin/weekOfYear';
import weekYear from 'dayjs/plugin/weekYear';
import { AuthProvider } from './AuthProvider';
import { $downloadEvent } from './store/download';
import { Routes } from './router/Routes';
import { watchInternetIsAvailable } from './store/app';
import { loadTactic } from './store/tactic';

dayjs.extend(customParseFormat);
dayjs.extend(advancedFormat);
dayjs.extend(weekday);
dayjs.extend(weekOfYear);
dayjs.extend(weekYear);
dayjs.extend(localeData);
dayjs.extend(localizedFormat);

const ENV = process.env.REACT_APP_BUILD_ENV || 'local';
export const VERSION = '1.2607.E';

Sentry.init({
  dsn: 'https://ad1c3a335a9697f00807ce3422cbfccf@o4508750318796800.ingest.de.sentry.io/4508750321221712',
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration(),
  ],
  // Tracing
  tracesSampleRate: 0.5, //  Capture 100% of the transactions
  // Session Replay
  replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
  replaysOnErrorSampleRate: 1.0,
  enabled: ENV !== 'local',
  // @ts-ignore
  release: window?.APP_VERSION || 'local',
  environment: ENV || 'local',
});

Amplify.configure(outputs);

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement,
);
const queryClient = new QueryClient();

//eslint-disable-next-line no-restricted-globals
const screenOrientation = screen?.orientation as any;

if (screenOrientation && screenOrientation?.lock) {
  screenOrientation.lock('landscape-primary').catch((e) => {
    console.error('Failed to lock screen orientation', e);
  });
} else {
  console.warn('Screen orientation lock is not supported by this browser.');
}

watchInternetIsAvailable();
void loadTactic();

root.render(
  <QueryClientProvider client={queryClient}>
    <BrowserRouter>
      <AuthProvider>
        <Routes />
      </AuthProvider>
    </BrowserRouter>
  </QueryClientProvider>,
);

window.addEventListener('beforeinstallprompt', (e) => {
  $downloadEvent.set(e);
});

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://cra.link/PWA
let isRefreshingServiceWorker = false;
serviceWorkerRegistration.register({
  onSuccess: () => {
    console.log('Service worker registration successful');
  },
  onUpdate: () => {
    navigator?.serviceWorker?.addEventListener('controllerchange', () => {
      if (isRefreshingServiceWorker) {
        return;
      }
      console.log('Service worker updated, refreshing page 3...');
      isRefreshingServiceWorker = true;
      window.location.reload();
    });
  },
});

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
// reportWebVitals(console.log)
